import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Tooltip from '@/components/ui/Tooltip/Tooltip';

interface DOMTooltipProps {
  target: HTMLElement;
}

type Side = 'top' | 'right' | 'bottom' | 'left';

// AIDEV-NOTE: Bridges a server-rendered DOM node to a Radix Tooltip. The Trigger
// is rendered via createPortal *inside* the foreign element so floating-ui
// positions against the real bounding rect AND React still owns the node
// (so unmount cleanup works). Hover/focus/Escape on the foreign node drive open state.
const HOVER_GRACE_MS = 100;

const DOMTooltip = ({ target }: DOMTooltipProps) => {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState(target.dataset.tooltip ?? '');
  const [side, setSide] = useState<Side>((target.dataset.tooltipSide ?? 'top') as Side);
  const closeTimerRef = useRef<number>();
  const contentRef = useRef<HTMLDivElement>(null);

  // AIDEV-NOTE: The Trigger's absolute positioning anchors to the nearest positioned
  // ancestor; ensure that's the target itself by promoting static targets to relative.
  useEffect(() => {
    const computed = window.getComputedStyle(target).position;
    // eslint-disable-next-line no-param-reassign
    if (computed === 'static') target.style.position = 'relative';
  }, [target]);

  // AIDEV-NOTE: Watch data-tooltip / data-tooltip-side for live updates so callers can
  // swap the label by mutating the attribute (e.g. jQuery `.attr('data-tooltip', ...)`).
  // Also watch style/class and close when the target loses its box: a target hidden under
  // the pointer (e.g. jQuery .hide() swapping paired anchors, as Main Ideas once did) never
  // fires mouseleave, and its collapsed rect pins the still-open tooltip to the viewport origin.
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setLabel(target.dataset.tooltip ?? '');
      setSide((target.dataset.tooltipSide ?? 'top') as Side);
      if (target.getClientRects().length === 0) {
        // Skip the exit animation: it keeps the content mounted while floating-ui
        // re-measures the collapsed trigger, flashing the tooltip at the viewport origin.
        const popperWrapper = contentRef.current?.parentElement;
        if (popperWrapper) popperWrapper.style.visibility = 'hidden';
        setOpen(false);
      }
    });
    observer.observe(target, {
      attributeFilter: ['class', 'data-tooltip', 'data-tooltip-side', 'style'],
      attributes: true,
    });
    return () => observer.disconnect();
  }, [target]);

  // AIDEV-NOTE: mouseleave uses a grace period so the pointer can travel from the
  // target to the tooltip content (portaled elsewhere in the DOM) without closing.
  // Content pointer handlers below cancel/trigger the close to complete the bridge.
  useEffect(() => {
    const show = () => {
      window.clearTimeout(closeTimerRef.current);
      setOpen(true);
    };
    const hide = () => {
      closeTimerRef.current = window.setTimeout(() => setOpen(false), HOVER_GRACE_MS);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };

    target.addEventListener('mouseenter', show);
    target.addEventListener('mouseleave', hide);
    target.addEventListener('focusin', show);
    target.addEventListener('focusout', hide);
    target.addEventListener('keydown', onKey);

    return () => {
      window.clearTimeout(closeTimerRef.current);
      target.removeEventListener('mouseenter', show);
      target.removeEventListener('mouseleave', hide);
      target.removeEventListener('focusin', show);
      target.removeEventListener('focusout', hide);
      target.removeEventListener('keydown', onKey);
    };
  }, [target]);

  const handleContentPointerEnter = () => {
    window.clearTimeout(closeTimerRef.current);
  };

  const handleContentPointerLeave = () => {
    setOpen(false);
  };

  return (
    <Tooltip.Provider delayDuration={0}>
      <Tooltip open={open} onOpenChange={setOpen}>
        {createPortal(
          <Tooltip.Trigger asChild>
            <span
              aria-hidden
              style={{
                bottom: 0,
                left: 0,
                pointerEvents: 'none',
                position: 'absolute',
                right: 0,
                top: 0,
              }}
            />
          </Tooltip.Trigger>,
          target,
        )}
        <Tooltip.Portal>
          {/* AIDEV-NOTE: Radix's hideWhenDetached can't cover the vanished-target case —
              floating-ui's hide middleware only detects clipped references, and a display:none
              target collapses to an unclipped 0x0 rect at the origin. The observer above hides
              the popper wrapper imperatively instead. */}
          <Tooltip.Content
            ref={contentRef}
            showArrow
            side={side}
            sideOffset={4}
            theme="white"
            onPointerEnter={handleContentPointerEnter}
            onPointerLeave={handleContentPointerLeave}
          >
            {label}
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip>
    </Tooltip.Provider>
  );
};

export default DOMTooltip;
